/**
 * Every user-visible string resolves through here. Nothing in this surface
 * hardcodes prose — if a string is missing, add it to product/copy.json first
 * so iOS and Server inherit it.
 */
import copyJson from '@/generated/product/copy.json'
import { bridge } from './bridge'

const strings = copyJson.en

type RawKey = keyof typeof strings
/**
 * Base of a plural pair: `accounts.row.transactions_one` becomes
 * `accounts.row.transactions`. The indirection through a type parameter is
 * what makes the conditional distribute across the union.
 */
type StripPluralSuffix<K> = K extends `${infer B}_one` ? B : never
type PluralBase = StripPluralSuffix<RawKey>

export type CopyKey = RawKey | PluralBase

export type CopyParams = Record<string, string | number>

const missing = new Set<string>()

function lookup(key: string, params?: CopyParams): string | undefined {
  // A plural form wins over the base key when a count is supplied, so a key
  // can gain _one/_other later without any caller changing.
  if (params && typeof params.count === 'number') {
    const plural = (strings as Record<string, string>)[`${key}_${params.count === 1 ? 'one' : 'other'}`]
    if (plural !== undefined) return plural
  }
  return (strings as Record<string, string>)[key]
}

function interpolate(template: string, params?: CopyParams): string {
  if (!params) return template
  return template.replace(/\{(\w+)\}/g, (whole, name: string) =>
    name in params ? String(params[name]) : whole,
  )
}

/** Resolve a copy key. Unknown keys are reported, never rendered as prose. */
export function t(key: CopyKey, params?: CopyParams): string {
  const template = lookup(key, params)
  if (template === undefined) {
    if (!missing.has(key)) {
      missing.add(key)
      bridge.log(`copy: missing key "${key}" — add it to product/copy.json`)
      if (import.meta.env.DEV) console.error(`copy: missing key "${key}"`)
    }
    return key
  }
  return interpolate(template, params)
}

/**
 * Resolve a key the Server chose (recommendation copyKey, error copyKey,
 * milestone copyKey). Falls back to `error.generic` per error.schema.json
 * rather than rendering a raw key at the user.
 */
export function tServer(key: string, params?: CopyParams, fallback: CopyKey = 'error.generic'): string {
  const template = lookup(key, params)
  if (template === undefined) return t(fallback, params)
  return interpolate(template, params)
}

export function hasCopy(key: string): boolean {
  return lookup(key) !== undefined
}
